import { FormattedMessage } from 'react-intl';
import { PREPARATION_STAGES, type PreparationStage } from '../../types';
import { stageRank } from '../stages';

interface Props {
  stage: PreparationStage | null;
  onSelect: (stage: PreparationStage) => void;
}

export default function StageStrip({ stage, onSelect }: Props) {
  const reached = stage === null ? -1 : stageRank(stage);

  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-3">
      {PREPARATION_STAGES.map((option, index) => {
        const isOurs = option === 'portal_ready';
        const isCurrent = index === reached;

        return (
          <li key={option} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onSelect(option)}
              aria-current={isCurrent ? 'step' : undefined}
              className={stepClasses({ isOurs, isPast: index < reached, isCurrent })}
            >
              <span className="tabular-nums opacity-60">{index + 1}</span>
              <FormattedMessage id={`preparation.strip.${option}`} />
            </button>
            {index < PREPARATION_STAGES.length - 1 && !isOurs && (
              <span className="text-slate-400" aria-hidden="true">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="m7 4 5 6-5 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function stepClasses({
  isOurs,
  isPast,
  isCurrent,
}: {
  isOurs: boolean;
  isPast: boolean;
  isCurrent: boolean;
}): string {
  const base =
    'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300';

  if (isOurs) return `${base} bg-brand-50 text-brand-800 ring-1 ring-brand-300 hover:bg-brand-100`;
  if (isCurrent) return `${base} bg-slate-900 text-white`;
  if (isPast) return `${base} bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800`;

  return `${base} bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900`;
}
